import { BufferAttribute } from 'three'
import { BufferGeometry } from 'three/src/core/BufferGeometry'

import { Vec2, Vec3, Vec4 } from './helpers'

/** How many times to subdivide planes in the x and y directions each */
const planeSegments = 4

/** How many times to subdivide cylinders' circle geometries */
const cylinderUSegments = 64

/** How many times to subdivide cylinders' shell geometries */
const cylinderVSegments = 4

/** The number of angled segments to divide cones into */
const angleSegments = 180

/** The number of radial segments to divide cones into */
const radialSegments = 16

/**
 * Creates a BufferGeometry object with the provided buffer attribute arrays
 * @param attributes A map from attribute names to arrays of vectors
 */
const createBufferGeometry = (
  attributes: Record<string, (Vec2 | Vec3 | Vec4)[]>
) => {
  const result = new BufferGeometry()
  Object.entries(attributes).forEach(([key, value]) => {
    result.setAttribute(
      key,
      new BufferAttribute(new Float32Array(value.flat()), value[0].length)
    )
  })
  return result
}

//------------------------------------------------------------------------------

const createPlaneAttributes = () => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()

  for (let x = 0; x < planeSegments; x++) {
    for (let y = 0; y < planeSegments; y++) {
      const u0 = x / planeSegments
      const v0 = y / planeSegments
      const u1 = (x + 1) / planeSegments
      const v1 = (y + 1) / planeSegments

      position.push([u0, v0, 0.0], [u1, v0, 0.0], [u1, v1, 0.0])
      position.push([u1, v1, 0.0], [u0, v1, 0.0], [u0, v0, 0.0])

      normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])
      normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])

      uv.push([u0, v0], [u1, v0], [u1, v1])
      uv.push([u1, v1], [u0, v1], [u0, v0])
    }
  }

  return { position, normal, uv }
}

const createShellAttributes = () => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()

  for (let i = 0; i < cylinderUSegments; i++) {
    for (let j = 0; j < cylinderVSegments; j++) {
      const uA = i / cylinderUSegments
      const uB = (i + 1) / cylinderUSegments

      const angleA = 2.0 * Math.PI * uA
      const angleB = 2.0 * Math.PI * uB

      const aX = -Math.cos(angleA)
      const aZ = Math.sin(angleA)
      const bX = -Math.cos(angleB)
      const bZ = Math.sin(angleB)
      const y0 = j / cylinderVSegments
      const y1 = (j + 1) / cylinderVSegments

      position.push([aX, y0, aZ], [bX, y0, bZ], [bX, y1, bZ])
      position.push([bX, y1, bZ], [aX, y1, aZ], [aX, y0, aZ])

      normal.push([aX, 0, aZ], [bX, 0, bZ], [bX, 0, bZ])
      normal.push([bX, 0, bZ], [aX, 0, aZ], [aX, 0, aZ])

      uv.push([uA, y0], [uB, y0], [uB, y1])
      uv.push([uB, y1], [uA, y1], [uA, y0])
    }
  }

  return { position, normal, uv }
}

const createCircleAttributes = () => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()
  const uv2 = new Array<Vec2>()

  for (let i = 0; i < cylinderUSegments; i++) {
    const uA = i / cylinderUSegments
    const uB = (i + 1) / cylinderUSegments
    const angleA = 2.0 * Math.PI * uA
    const angleB = 2.0 * Math.PI * uB

    const aX = Math.cos(angleA)
    const aY = Math.sin(angleA)
    const bX = Math.cos(angleB)
    const bY = Math.sin(angleB)
    position.push([0.0, 0.0, 0.0], [aX / 2, aY / 2, 0.0], [bX / 2, bY / 2, 0])
    normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])
    const aU = (aX + 1.0) / 2.0
    const aV = (aY + 1.0) / 2.0
    const bU = (bX + 1.0) / 2.0
    const bV = (bY + 1.0) / 2.0
    uv.push([0.5, 0.5], [aU, aV], [bU, bV])
    uv2.push([uA, 0.0], [uA, 1.0], [uB, 1.0])
  }

  return { position, normal, uv, uv2 }
}

const createConeAttributes = () => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()
  const uv2 = new Array<Vec2>()
  for (let j = 0; j < radialSegments; j++) {
    for (let i = 0; i < angleSegments; i++) {
      const r1 = j / radialSegments
      const r2 = (j + 1) / radialSegments
      const uA = i / angleSegments
      const uB = (i + 1) / angleSegments
      const angleA = 2.0 * Math.PI * uA
      const angleB = 2.0 * Math.PI * uB

      const aX = -Math.sin(angleA)
      const aY = Math.cos(angleA)
      const bX = -Math.sin(angleB)
      const bY = Math.cos(angleB)
      if (j === 0) {
        position.push(
          [0.0, 0.0, 0.0],
          [r2 * aX, r2 * aY, 0.0],
          [r2 * aX, r2 * aY, 0.0]
        )
        position.push(
          [0.0, 0.0, 0.0],
          [r2 * aX, r2 * aY, 0.0],
          [r2 * bX, r2 * bY, 0.0]
        )
        position.push(
          [0.0, 0.0, 0.0],
          [r2 * bX, r2 * bY, 0.0],
          [r2 * bX, r2 * bY, 0.0]
        )
        normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])
        normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])
        normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])

        uv.push([uA, r1], [uA, r2], [(uA + uB) / 2, r1])
        uv.push([(uA + uB) / 2, r1], [uA, r2], [uB, r2])
        uv.push([(uA + uB) / 2, r1], [uB, r2], [uB, r1])

        uv2.push([uA, r1], [uA, r2], [(uA + uB) / 2, r1])
        uv2.push([(uA + uB) / 2, r1], [uA, r2], [uB, r2])
        uv2.push([(uA + uB) / 2, r1], [uB, r2], [uB, r1])
      } else {
        position.push(
          [r1 * aX, r1 * aY, 0.0],
          [r2 * aX, r2 * aY, 0.0],
          [r2 * bX, r2 * bY, 0.0]
        )
        normal.push([0.0, 1.0, 0.0], [0.0, 1.0, 0.0], [0.0, 1.0, 0.0])
        uv.push([uA, r1], [uA, r2], [uB, r2])
        uv2.push([uA, r1], [uA, r2], [uB, r2])

        position.push(
          [r1 * aX, r1 * aY, 0.0],
          [r2 * bX, r2 * bY, 0.0],
          [r1 * bX, r1 * bY, 0.0]
        )
        normal.push([0.0, 1.0, 0.0], [0.0, 1.0, 0.0], [0.0, 1.0, 0.0])
        uv.push([uA, r1], [uB, r2], [uB, r1])
        uv2.push([uA, r1], [uB, r2], [uB, r1])
      }
    }
  }

  return { position, normal, uv, uv2 }
}

const createSphereAttributes = (isHalfSphere = false) => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()

  const latitudeBands = cylinderVSegments * 2
  const longitudeBands = cylinderUSegments

  // For half sphere, we only go from 0 to PI/2 (top half)
  const phiStart = 0
  const phiLength = isHalfSphere ? Math.PI / 2 : Math.PI

  const angle = (2 * Math.PI) / longitudeBands

  // Create vertices
  for (let latNumber = 0; latNumber <= latitudeBands; latNumber++) {
    const theta = (latNumber * phiLength) / latitudeBands + phiStart
    const sinTheta = Math.sin(theta)
    const cosTheta = Math.cos(theta)

    const longitudeStart = 0
    const longitudeEnd = longitudeBands

    for (
      let longNumber = longitudeStart;
      longNumber <= longitudeEnd;
      longNumber++
    ) {
      const phi = longNumber * angle
      const sinPhi = Math.sin(phi)
      const cosPhi = Math.cos(phi)

      const x = cosPhi * sinTheta
      const y = cosTheta
      const z = sinPhi * sinTheta

      const u = longNumber / longitudeBands
      const v = 1.0 - latNumber / latitudeBands

      if (latNumber < latitudeBands && longNumber < longitudeEnd) {
        const p1 = [x, y, z] as Vec3
        const p2 = [
          Math.cos(phi + angle) * sinTheta,
          cosTheta,
          Math.sin(phi + angle) * sinTheta,
        ] as Vec3
        const p3 = [
          cosPhi * Math.sin(theta + phiLength / latitudeBands),
          Math.cos(theta + phiLength / latitudeBands),
          sinPhi * Math.sin(theta + phiLength / latitudeBands),
        ] as Vec3

        position.push(p1, p2, p3)

        normal.push(p1, p2, p3)

        uv.push([u, v], [u + 1 / longitudeBands, v], [u, v - 1 / latitudeBands])

        const p4 = [
          Math.cos(phi + angle) * sinTheta,
          cosTheta,
          Math.sin(phi + angle) * sinTheta,
        ] as Vec3
        const p5 = [
          Math.cos(phi + angle) * Math.sin(theta + phiLength / latitudeBands),
          Math.cos(theta + phiLength / latitudeBands),
          Math.sin(phi + angle) * Math.sin(theta + phiLength / latitudeBands),
        ] as Vec3
        const p6 = [
          cosPhi * Math.sin(theta + phiLength / latitudeBands),
          Math.cos(theta + phiLength / latitudeBands),
          sinPhi * Math.sin(theta + phiLength / latitudeBands),
        ] as Vec3

        position.push(p4, p5, p6)

        normal.push(p4, p5, p6)

        uv.push(
          [u + 1 / longitudeBands, v],
          [u + 1 / longitudeBands, v - 1 / latitudeBands],
          [u, v - 1 / latitudeBands]
        )
      }
    }
  }

  return { position, normal, uv }
}

export const get3DConeGeometry = (
  innerRadius: number,
  outerRadius: number,
  coneHeight: number
) => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()
  const uv2 = new Array<Vec2>()

  const scaledOuterRadius = outerRadius / Math.max(innerRadius, outerRadius)
  const scaledInnerRadius = innerRadius / Math.max(innerRadius, outerRadius)

  for (let i = 0; i < angleSegments; i++) {
    const uA = i / angleSegments
    const uB = (i + 1) / angleSegments
    const angleA = 2.0 * Math.PI * uA
    const angleB = 2.0 * Math.PI * uB

    const aX = -Math.cos(angleA)
    const aZ = Math.sin(angleA)
    const bX = -Math.cos(angleB)
    const bZ = Math.sin(angleB)

    const ax1 = outerRadius * aX
    const ay1 = 0.0
    const az1 = outerRadius * aZ

    const bx1 = outerRadius * bX
    const by1 = 0.0
    const bz1 = outerRadius * bZ

    const ax2 = innerRadius * aX
    const ay2 = coneHeight
    const az2 = innerRadius * aZ

    const bx2 = innerRadius * bX
    const by2 = coneHeight
    const bz2 = innerRadius * bZ

    // cross product, derive normal
    const ux = bx1 - ax1
    const uy = by1 - ay1
    const uz = bz1 - az1

    const vx = ax2 - ax1
    const vy = ay2 - ay1
    const vz = az2 - az1

    const cpx = uy * vz - uz * vy
    const cpy = -(ux * vz - vx * uz)
    const cpz = ux * vy - vx * uz

    const normalizationFactor = Math.sqrt(cpx * cpx + cpy * cpy + cpz * cpz)

    const nx = cpx / normalizationFactor
    const ny = cpy / normalizationFactor
    const nz = cpz / normalizationFactor

    position.push([ax1, ay1, az1], [bx1, by1, bz1], [ax2, ay2, az2])
    normal.push([nx, ny, nz], [nx, ny, nz], [nx, ny, nz])

    uv.push(
      [uA, scaledOuterRadius],
      [uB, scaledOuterRadius],
      [uA, scaledInnerRadius]
    )
    uv2.push(
      [uA, scaledOuterRadius],
      [uB, scaledOuterRadius],
      [uA, scaledInnerRadius]
    )

    position.push([ax2, ay2, az2], [bx1, by1, bz1], [bx2, by2, bz2])
    normal.push([nx, ny, nz], [nx, ny, nz], [nx, ny, nz])

    uv.push(
      [uA, scaledInnerRadius],
      [uB, scaledOuterRadius],
      [uB, scaledInnerRadius]
    )
    uv2.push(
      [uA, scaledInnerRadius],
      [uB, scaledOuterRadius],
      [uB, scaledInnerRadius]
    )
  }
  return createBufferGeometry({ position, normal, uv, uv2: uv })
}

//------------------------------------------------------------------------------

export const getDeckGeometry = (
  length: number,
  startWidth: number,
  endWidth: number
) => {
  const maxWidth = Math.max(startWidth, endWidth)
  const t1x1 = maxWidth / 2 - startWidth / 2
  const t1x2 = maxWidth / 2 + endWidth / 2
  const t1x3 = maxWidth / 2 - endWidth / 2

  const t1y1 = 0
  const t1y2 = length
  const t1y3 = length

  const t2x1 = maxWidth / 2 - startWidth / 2
  const t2x2 = maxWidth / 2 + startWidth / 2
  const t2x3 = maxWidth / 2 + endWidth / 2

  const t2y1 = 0
  const t2y2 = 0
  const t2y3 = length

  const position: Array<Vec3> = [
    [t1x1, t1y1, 0],
    [t1x2, t1y2, 0],
    [t1x3, t1y3, 0],
    [t2x1, t2y1, 0],
    [t2x2, t2y2, 0],
    [t2x3, t2y3, 0],
  ]

  const normal: Array<Vec3> = [
    [0, 0, 1.0],
    [0, 0, 1.0],
    [0, 0, 1.0],
    [0, 0, 1.0],
    [0, 0, 1.0],
    [0, 0, 1.0],
  ]

  const uv: Array<Vec2> = position.map(([x, y]) => [x / maxWidth, y / length])

  return createBufferGeometry({ position, normal, uv, uv2: uv })
}

export const createHalfShellAttributes = (
  height: number,
  outerRadius: number,
  thickness: number
) => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()

  const innerRadius = outerRadius - thickness

  for (let i = cylinderUSegments / 2; i < cylinderUSegments; i++) {
    const uA = i / cylinderUSegments
    const uB = (i + 1) / cylinderUSegments
    const y0 = 0
    const y1 = height

    const angleA = 2.0 * Math.PI * uA
    const angleB = 2.0 * Math.PI * uB

    const aX = -Math.cos(angleA)
    const aZ = Math.sin(angleA)
    const bX = -Math.cos(angleB)
    const bZ = Math.sin(angleB)

    const outerXA = aX * outerRadius
    const outerZA = aZ * outerRadius
    const outerXB = bX * outerRadius
    const outerZB = bZ * outerRadius

    const innerXA = aX * innerRadius
    const innerZA = aZ * innerRadius
    const innerXB = bX * innerRadius
    const innerZB = bZ * innerRadius

    if (y1 === height) {
      position.push(
        [outerXB, height, outerZB],
        [innerXA, height, innerZA],
        [outerXA, height, outerZA]
      )
      position.push(
        [innerXB, height, innerZB],
        [innerXA, height, innerZA],
        [outerXB, height, outerZB]
      )
      normal.push([0, 1, 0], [0, 1, 0], [0, 1, 0])
      normal.push([0, 1, 0], [0, 1, 0], [0, 1, 0])
    }

    if (y0 === 0) {
      position.push(
        [outerXA, 0, outerZA],
        [innerXA, 0, innerZA],
        [outerXB, 0, outerZB]
      )
      position.push(
        [outerXB, 0, outerZB],
        [innerXA, 0, innerZA],
        [innerXB, 0, innerZB]
      )
      normal.push([0, -1, 0], [0, -1, 0], [0, -1, 0])
      normal.push([0, -1, 0], [0, -1, 0], [0, -1, 0])
    }
    if (angleA === Math.PI) {
      position.push(
        [outerXA, height, outerZA],
        [innerXA, 0, innerZA],
        [outerXA, 0, outerZA]
      )
      position.push(
        [outerXA, height, outerZA],
        [innerXA, height, innerZA],
        [innerXA, 0, innerZA]
      )
      normal.push([0, 0, 1], [0, 0, 1], [0, 0, 1])
      normal.push([0, 0, 1], [0, 0, 1], [0, 0, 1])
    }
    if (angleB === 2 * Math.PI) {
      position.push(
        [outerXB, 0, outerZB],
        [innerXB, 0, innerZB],
        [outerXB, height, outerZB]
      )
      position.push(
        [innerXB, 0, innerZB],
        [innerXB, height, innerZB],
        [outerXB, height, outerZB]
      )
      normal.push([0, 0, 1], [0, 0, 1], [0, 0, 1])
      normal.push([0, 0, 1], [0, 0, 1], [0, 0, 1])
    }

    // Outer shell
    position.push(
      [outerXA, y0, outerZA],
      [outerXB, y0, outerZB],
      [outerXB, y1, outerZB]
    )
    position.push(
      [outerXB, y1, outerZB],
      [outerXA, y1, outerZA],
      [outerXA, y0, outerZA]
    )
    normal.push(
      [outerXA, 0, outerZA],
      [outerXB, 0, outerZB],
      [outerXB, 0, outerZB]
    )
    normal.push(
      [outerXB, 0, outerZB],
      [outerXA, 0, outerZA],
      [outerXA, 0, outerZA]
    )

    // Inner Shell
    position.push(
      [innerXB, y1, innerZB],
      [innerXB, y0, innerZB],
      [innerXA, y0, innerZA]
    )
    position.push(
      [innerXA, y0, innerZA],
      [innerXA, y1, innerZA],
      [innerXB, y1, innerZB]
    )
    normal.push(
      [-innerXA, 0, -innerZA],
      [-innerXB, 0, -innerZB],
      [-innerXB, 0, -innerZB]
    )
    normal.push(
      [-innerXB, 0, -innerZB],
      [-innerXA, 0, -innerZA],
      [-innerXA, 0, -innerZA]
    )
  }
  return createBufferGeometry({ position, normal })
}

export const planeGeometry = (() => {
  const { position, normal, uv } = createPlaneAttributes()
  return createBufferGeometry({ position, normal, uv, uv2: uv })
})()

export const xWrappedShellGeometry = (() => {
  const { position, normal, uv } = createShellAttributes()
  return createBufferGeometry({ position, normal, uv, uv2: uv })
})()

export const yWrappedShellGeometry = (() => {
  const { position, normal, uv } = createShellAttributes()
  // Reverse all these as the normals flip directions
  const newPositions = position.map<Vec3>(([x, y, z]) => [y, x, z]).reverse()
  const newNormals = normal.map<Vec3>(([x, y, z]) => [y, x, z]).reverse()
  const newUV2s = uv.reverse() // reverse happens in place
  const newUVs = uv.map<Vec2>(([u, v]) => [v, u])
  return createBufferGeometry({
    position: newPositions,
    normal: newNormals,
    uv: newUVs,
    uv2: newUV2s,
  })
})()

export const cartesianCircleGeometry = (() => {
  const { position, normal, uv, uv2 } = createCircleAttributes()
  return createBufferGeometry({ position, normal, uv, uv2 })
})()

export const radialConeGeometry = (() => {
  const { position, normal, uv, uv2 } = createConeAttributes()
  return createBufferGeometry({ position, normal, uv, uv2 })
})()

export const sphereGeometry = (() => {
  const { position, normal, uv } = createSphereAttributes(true)
  return createBufferGeometry({ position, normal, uv, uv2: uv })
})()
